/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// eslint-disable-next-line import/no-cycle
import api from '@src/api';
import { Observable } from 'rxjs';
import { Action } from 'redux-actions';

export interface IPunchInRecord {
	duty: true,
  ip_setting_id: string;
  is_overtime: null | boolean;
  latitude: string;
  location_id: string;
  longitude: string;
  non_overtime_reason: string;
  punch_in_type: string;
  reason: string;
  // 離線打卡 retry 時才帶上，BE 看到會以這個時間當打卡時間（UTC ISO 8601），而不是收到 request 的時間。
  punch_datetime?: string;
  // 離線打卡 retry 時才帶上，給 BE 做初步歸因（'http_502' / 'timeout' / 'network_error'）。上限 64 chars。
  deferred_reason?: string;
}

export interface IShiftTimeRange {
  range_from: string; // e.g. "9:00"
  range_to: string; // e.g. "9:30"
}

export interface IShiftUnpaidDuration {
  unpaid_duration_start_hour: number;
  unpaid_duration_start_minute: number;
  unpaid_duration_end_hour: number;
  unpaid_duration_end_minute: number;
}

export interface IShiftInfo {
  shift_id: string;
  shift_name: string;
  punch_in_required: boolean;
  shift_time?: {
    start: IShiftTimeRange;
    end: IShiftTimeRange;
  };
  actual_start_time: string | null;
  actual_end_time: string | null;
  actual_end_time_expected?: string | null;
  overtime_before?: string;
  overtime_after?: string;
  is_rollover_previous_day?: boolean;
  daily_rollover_hour?: number;
  daily_rollover_minute?: number;
  unpaid_durations?: IShiftUnpaidDuration[];
  // 前端 cache fallback 標記（stale 模式下無法驗證新鮮度的班別）
  isStaleUnverified?: boolean;
}

export const getPunchIn = (otherConfigs?: Record<string, unknown>): Observable<Action> => (
	api.get('punch-in-info/', undefined, otherConfigs)
);

export const getSuitableShifts = (): Observable<IShiftInfo[]> => (
	api.get<IShiftInfo[]>('punch-in-info/suitable-shifts')
);

export const getScheduleShifts = (): Observable<IShiftInfo[]> => (
	api.get<IShiftInfo[]>('punch-in-info/schedule-shifts')
);

export const getDailyShifts = (payload: { date: string }): Observable<IShiftInfo[]> => (
	api.get<IShiftInfo[]>('punch-in-info/daily-shifts', payload)
);

export const postPunchInRecord = (
	payload: IPunchInRecord,
	otherConfigs?: Record<string, unknown>,
): Observable<Action> => (
	api.post('punch-in-records/', payload, otherConfigs)
);

export interface IPunchInQueueRequest extends IPunchInRecord {
	// enqueue 預設「不」帶 punch_datetime，用 server 收到時間。
	// 只有 5xx/timeout 落 local queue 重送時才補上打卡當下時間（UTC ISO 8601），避免延後送出被判遲到。
	punch_datetime?: string;
}

export interface IPunchInQueueTask {
	// 後端 202 回傳的 Firestore document id（= 要監聽的 doc），存成 record.docKey
	document_id: string;
	document_created_at?: string;
}

export const postPunchInRecordQueue = (
	payload: IPunchInQueueRequest,
	otherConfigs?: Record<string, unknown>,
): Observable<Action> => (
	api.post('punch-in-records/enqueue', payload, otherConfigs)
);

export type DeviceAbnormalType = 'none' | 'other_user_device' | 'exceed_allowed_device_count';

export interface IDeviceAbnormalityResult {
	// BE 已套用企業「偵測到代打卡時」設定：只有設為阻擋且裝置不合法才會是 true
	is_abnormal: boolean;
	abnormal_type: DeviceAbnormalType | null;
}

export const getDeviceAbnormality = (otherConfigs?: Record<string, unknown>): Promise<IDeviceAbnormalityResult> => (
	api.get('punch-in-device-bindings/check-device-abnormality', undefined, otherConfigs)
);

export const getDayShifts = (payload): Observable<Action> => (
	api.get('punch-in-info/day_shifts', payload)
);

export const getAllShifts = (payload): Observable<Action> => (
	api.get('punch-in-info/all-shifts', payload)
);

export interface RestPunchRecord {
  id: string;
  ip_or_location_name: string;
  on_break: boolean;
  punch_at: string;
  punch_in_type: 'ip' | 'location' | 'time_clock_machine';
  shift_name: string;
  shift_id?: string;
  record_created_at?: string;
}

export const getRestPunchRecord = (payload?: { query_date?: string }): Observable<RestPunchRecord[]> => (
	api.get<RestPunchRecord[]>('punch-in-records/rest-records', payload)
);

type BaseRestPunchRecord = {
  on_break: boolean;
  shift_id?: string;
};

type LocationRestPunchRecord = BaseRestPunchRecord & {
  punch_in_type: 'location';
  location_id: string;
  latitude: number;
  longitude: number;
  reason?: string;
};

type IPRestPunchRecord = BaseRestPunchRecord & {
  punch_in_type: 'ip';
  ip_setting_id: string;
};

type QRcodeRestPunchRecord = BaseRestPunchRecord & {
  punch_in_type: 'qrcode';
  qr_code_id: string;
  verify_code: string;
};

export type IRestPunchRecord = LocationRestPunchRecord | IPRestPunchRecord | QRcodeRestPunchRecord;

export const postRestPunchRecord = (payload: IRestPunchRecord): Observable<Action> => (
	api.post('punch-in-records/rest-records', payload)
);

export const deleteRestPunchRecord = (payload: { id: string }): Observable<Action> => (
	// eslint-disable-next-line dot-notation
	api.delete(`punch-in-records/rest-records/${payload.id}`)
);

type GetPunchInRule = {
  date: string
}
export const getPunchInRule = (payload: GetPunchInRule): Observable<Action> => (
	api.get('punch-in-info/punch-in-rule-set', payload)
);

type GetPunchInShift = {
  dateTime: string
}
export const getPunchInShift = (payload: GetPunchInShift): Observable<Action> => (
	api.get(`punch-in-info/shift?query_datetime=${payload.dateTime}`)
);

type GetPunchInShiftsByRange = {
  startDateTime: string
  endDateTime: string
}
export const getPunchInShiftsByRange = (payload: GetPunchInShiftsByRange): Observable<Action> => (
	api.get(`punch-in-info/shifts-by-range?start_datetime=${payload.startDateTime}&end_datetime=${payload.endDateTime}`)
);

export type DecodeTimeClockInfoPayload = {
  enc: string;
  mac: string;
  sid: string;
  uid: string;
};
export const getTimeClockDecodeInfo = (payload: DecodeTimeClockInfoPayload): Promise<unknown> => (
	api.get('punch-in-time-clocks/decode-info', payload)
);

export type TimeClockPunchPayload = {
  enc: string;
  mac: string;
  sid: string;
  uid: string;
  duty?: boolean | null;
  shift_id?: string | null;
  record_type?: 'work' | 'rest' | null;
  // 離線打卡 retry 時才帶上，BE 看到會以這個時間當打卡時間（UTC ISO 8601），並豁免 enc/counter 的時效驗證
  punch_datetime?: string;
  // 離線打卡 retry 時才帶上，給 BE 做初步歸因（'http_502' / 'timeout' / 'network_error'）。上限 64 chars。
  deferred_reason?: string;
};

export type TimeClockPunchResult = {
  id: string; // 打卡紀錄 id，供成功頁「去修正」CTA 導頁用
  shift_name?: string | null;
  punch_at: string;
  // BE 依班別自動判斷上下班：true 上班、false 下班。BE 尚未上線前不會回，前端沿用舊行為視為上班
  duty?: boolean;
  record_type?: 'work' | 'rest';
  record_created_at?: string;
};

export const postTimeClockPunchQueue = (
	payload: TimeClockPunchPayload,
	otherConfigs?: Record<string, unknown>,
): Promise<IPunchInQueueTask> => {
	// Keep null in local storage so clearing a selection is durable, but omit it on the wire.
	const { duty, shift_id, record_type, ...basePayload } = payload;
	const requestPayload = {
		...basePayload,
		...(duty != null ? { duty } : {}),
		...(shift_id != null ? { shift_id } : {}),
		...(record_type != null ? { record_type } : {}),
	};
	return api.post(
		'punch-in-records/punch-by-machine/enqueue', requestPayload, { suppressErrorToast: true, ...otherConfigs },
	);
};

export const postTimeClockPunch = (
	payload: TimeClockPunchPayload,
	otherConfigs?: Record<string, unknown>,
): Promise<TimeClockPunchResult> => (
	api.post('punch-in-records/punch-by-machine', payload, { suppressErrorToast: true, ...otherConfigs })
);
